import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Joyride, { Step, Placement } from 'react-joyride';
import useWindowWidth from '../../../../hooks/useWindowWidth';
import { joyrideStyles } from '../../../../utils/joyrideStyles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { updateCurrentUserInfo } from '../../../../services/auth';
import { Capacitor } from '@capacitor/core';

interface FlashcardsTourProps {
  isFlipped: boolean;
}

const FlashcardsTour: React.FC<FlashcardsTourProps> = ({ isFlipped }) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const stepSelectors: (React.RefObject<HTMLElement> | string)[] = [
    '.button--reveal',
    'body',
    '.flashcard_actions',
    '.flashcard_actions',
    'body',
    '.button--flashcard-menu',
  ];

  const [isFamiliar, setIsFamiliar] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const windowWidth = useWindowWidth();
  const placementsDesktop: Placement[] = [
    'center' as Placement,
    'center' as Placement,
    'bottom' as Placement,
    'bottom' as Placement,
    'center' as Placement,
    'right' as Placement,
  ];
  const placementsMobile: Placement[] = [
    'center' as Placement,
    'center' as Placement,
    'top' as Placement,
    'top' as Placement,
    'center' as Placement,
    'top' as Placement,
  ];

  const renderContents = useCallback(
    (index: number) => {
      if (index === 0) {
        return (
          <>
            <div className="tour-wrapper_content">
              <h2>Welcome to Spaced-Repetition Flashcards on Ora</h2>
              <p>
                Would you like an overview? If you are familiar with Anki, it's
                pretty similar.
              </p>
            </div>
            <div
              className="tour-wrapper_footer"
              style={{
                display: 'flex',
                gap: '.5rem',
                flexWrap: 'nowrap',
                marginBottom: '.75rem',
              }}
            >
              <button
                className="button"
                onClick={() => {
                  setStepIndex(5);
                  setIsFamiliar(true);
                  const floater = document.querySelector(
                    '.__floater'
                  ) as HTMLDivElement;
                  if (floater) {
                    floater.style.display = 'none';
                  }
                }}
                tabIndex={0}
                aria-label="No"
              >
                No
              </button>
              <button
                className="button"
                onClick={() => {
                  setStepIndex(stepIndex + 1);
                  const floater = document.querySelector(
                    '.__floater'
                  ) as HTMLDivElement;
                  if (floater) {
                    floater.style.display = 'none';
                  }
                }}
                tabIndex={0}
                aria-label="Yes"
              >
                Yes
              </button>
            </div>
          </>
        );
      }
      if (index === 1) {
        return (
          <>
            <div className="tour-wrapper_content">
              <p>
                Spaced repetition helps you learn faster by showing concepts you
                get wrong more often than those you get right.
                <br />
                <br />
                Let's try it out.
              </p>
            </div>
            <div
              className="tour-wrapper_footer"
              style={{
                marginBottom: '.75rem',
              }}
            >
              <button
                className="button"
                onClick={() => {
                  setStepIndex(stepIndex + 1);
                }}
                tabIndex={0}
                aria-label="Next"
              >
                Next
              </button>
            </div>
          </>
        );
      }
      if (index === 2) {
        return (
          <>
            <div className="tour-wrapper_content">
              {Capacitor.isNativePlatform() || windowWidth <= 560 ? (
                <p>
                  Think of the answer to this flashcard. When you are ready to
                  see the answer, tap Reveal.
                </p>
              ) : (
                <p>
                  Think of the answer to this flashcard. When you are ready to
                  see the answer, press Spacebar.
                </p>
              )}
            </div>
          </>
        );
      }
      if (index === 3) {
        return (
          <>
            <div className="tour-wrapper_content">
              {Capacitor.isNativePlatform() || windowWidth <= 560 ? (
                <p>Now assess your answer. Did you get it right?</p>
              ) : (
                <p>
                  Now assess your answer. (Shortcuts: 1 for wrong, 2 for right)
                </p>
              )}
            </div>
          </>
        );
      }
      if (index === 4) {
        return (
          <>
            <div className="tour-wrapper_content">
              <p>
                That's all there is to it!
                <br />
                <br />
                Ora will quickly learn which concepts you need to review and
                when to show them to you.
              </p>
            </div>
            <div
              className="tour-wrapper_footer"
              style={{
                marginBottom: '.75rem',
              }}
            >
              <button
                className="button"
                onClick={() => {
                  setStepIndex(stepIndex + 1);
                  const floater = document.querySelector(
                    '.__floater'
                  ) as HTMLDivElement;
                  if (floater) {
                    floater.style.display = 'none';
                  }
                }}
                tabIndex={0}
                aria-label="No"
              >
                Next
              </button>
            </div>
          </>
        );
      }
      if (index === 5) {
        return (
          <>
            <div className="tour-wrapper_content">
              {isFamiliar ? (
                <>
                  Sounds good! You can always access help in the flashcard menu.
                  Enjoy!
                </>
              ) : (
                <>
                  You can always access help or additional features from the
                  flashcard menu. Enjoy!
                </>
              )}
            </div>
            <div className="tour-wrapper_footer">
              <button
                onClick={() => {
                  setShowTour(false);
                  const floater = document.querySelector(
                    '.__floater'
                  ) as HTMLDivElement;
                  if (floater) {
                    floater.style.display = 'none';
                  }
                }}
                className="button"
                style={{ marginBottom: '1rem' }}
              >
                Finish
              </button>
            </div>
          </>
        );
      }
    },
    [windowWidth, isFamiliar, stepIndex]
  );

  const handleSeenTour = useCallback(async () => {
    if (!currentUser) return;
    await updateCurrentUserInfo({
      onboardingTips: {
        ...currentUser?.onboardingTips,
        'flashcards-tour': true,
      },
    });
  }, [currentUser]);

  useEffect(() => {
    setShowTour(true);
    handleSeenTour();
  }, []);

  const handleTourEnd = useCallback(() => {
    setShowTour(false);
  }, []);

  useEffect(() => {
    if (stepIndex === 2 && isFlipped) {
      setStepIndex(3);
    }
  }, [stepIndex, isFlipped, setStepIndex]);

  useEffect(() => {
    if (stepIndex === 3 && !isFlipped) {
      setStepIndex(4);
    }
  }, [stepIndex, isFlipped, setStepIndex]);

  const steps = useMemo(() => {
    const areTargetsReady = stepSelectors.every((target) => {
      if (typeof target === 'string') {
        return true;
      } else {
        return target.current !== null;
      }
    });

    if (!areTargetsReady) return [];

    return stepSelectors.map((target, index) => {
      const stepTarget = typeof target === 'string' ? target : target.current!;
      return {
        target: stepTarget,
        placement:
          windowWidth > 900
            ? placementsDesktop[index]
            : placementsMobile[index],
        disableBeacon: true,
        content: (
          <div className="tour-wrapper tour-wrapper--flashcards">
            {renderContents(index)}
            <div className="blur-1"></div>
            <div className="blur-2"></div>
          </div>
        ),
      } as Step;
    });
  }, [stepSelectors, windowWidth, renderContents]);

  if (steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={showTour}
      stepIndex={stepIndex}
      continuous
      hideCloseButton={true}
      hideBackButton={true}
      disableScrolling={true}
      disableOverlay
      styles={joyrideStyles}
      callback={({ status }) => {
        if (status === 'finished' || status === 'skipped') {
          handleTourEnd();
        }
      }}
    />
  );
};

export default FlashcardsTour;
